import {useEffect, useState, useCallback} from 'react';
import {useInView} from 'react-intersection-observer';
import throttle from 'lodash.throttle';
import root from 'window-or-global';

type AnimateScrollOptions = 'scale' | 'translateY';

interface CheckSizeCallbackProps {
    top: number;
    bottom: number;
    viewportHeight: number;
    height: number;
    pxShowing: number;
    percentVisible: number;
}

export interface AnimateScrollProps {
    id: string;
    type: AnimateScrollOptions;
    threshold?: number | number[];
    checkSizeCallback?: (props: CheckSizeCallbackProps) => void;
    disableFromBottom?: boolean;
    disableFromTop?: boolean;
}

export interface AnimateScrollReturnProps {
    percentVisible: number;
    elId: string;
    ref: (node?: Element | null) => void;
}

const activeMapping: {[key: string]: boolean} = {};

const getPxShowing = (pos: number, height: number) => {
    let pxShowing = height;
    if (pos < 0 && Math.abs(pos) < height) {
        pxShowing = height + pos;
    } else if (pos < 0 && Math.abs(pos) >= height) {
        pxShowing = 0;
    }
    return pxShowing;
};

export const useScrollToAnimate = ({
    id,
    type,
    threshold = 0,
    checkSizeCallback,
    disableFromTop = false,
    disableFromBottom = false
}: AnimateScrollProps): AnimateScrollReturnProps => {
    const elId = `${id}-${type}`;

    /**
     * Performant interaction observer will trigger a single event when the threshold is met
     */
    const {ref, inView, entry} = useInView({
        threshold
    });

    const [percentVisible, setPercentVisible] = useState(0);

    const checkSize = useCallback(() => {
        const el = entry?.target || root.document.getElementById(elId);
        if (el) {
            const {top, height, bottom} = el.getBoundingClientRect();
            const viewportHeight = root.innerHeight || root.document.documentElement.clientHeight;
            let pos = viewportHeight - bottom < 0 ? viewportHeight - bottom : top;
            if (disableFromTop) {
                pos = viewportHeight - bottom;
            } else if (disableFromBottom) {
                pos = top;
            }
            const pxShowing = getPxShowing(pos, height);
            const percentShowing = pxShowing / height;

            setPercentVisible(percentShowing);

            // Optional callback function in case additional checks need to be done
            if (checkSizeCallback && typeof checkSizeCallback === 'function') {
                checkSizeCallback({
                    top,
                    bottom,
                    height,
                    pxShowing,
                    percentVisible: percentShowing,
                    viewportHeight
                });
            }
        }
    }, [entry, setPercentVisible, percentVisible, checkSizeCallback]);

    const checkSizeScroll = throttle(checkSize, 25, {leading: false});
    const checkSizeResize = throttle(checkSize, 1500, {leading: false});

    const cleanUp = () => {
        root.document.removeEventListener('scroll', checkSizeScroll);
        root.document.removeEventListener('resize', checkSizeResize);
        activeMapping[elId] = false;
    };

    //Add the scroll listener only when the element has reached the visiblity threshold and clean up when fully visible
    useEffect(() => {
        //Calculate the sizing after first render
        checkSize();

        let fullyVisible = false;
        if (percentVisible === 1) {
            fullyVisible = true;
        }

        if (inView && !activeMapping[elId] && !fullyVisible) {
            activeMapping[elId] = true;
            root.document.addEventListener('scroll', checkSizeScroll);
            root.document.addEventListener('resize', checkSizeResize);
        } else if (activeMapping[elId]) {
            cleanUp();
        }
        return cleanUp;
    }, [inView, percentVisible, entry]);

    return {
        percentVisible,
        ref,
        elId
    };
};
